import React, { createContext, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'sovrynn_theme';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

const getSystemTheme = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'system';
    } catch {
      return 'system';
    }
  });
  const [systemTheme, setSystemTheme] = useState('dark');

  const resolvedTheme = theme === 'system' ? systemTheme : theme;

  useEffect(() => {
    setSystemTheme(getSystemTheme());
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme);
    document.body.setAttribute('data-theme', resolvedTheme);
    const bg = resolvedTheme === 'light' ? '#F5F5F4' : '#07080A';
    document.documentElement.style.backgroundColor = bg;
  }, [resolvedTheme]);

  useEffect(() => {
    const mq = window.matchMedia && window.matchMedia('(prefers-color-scheme: light)');
    if (!mq) return;
    const handle = () => setSystemTheme(getSystemTheme());
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  const setTheme = (value) => {
    setThemeState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      console.warn('Could not persist theme', e);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
